import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { get, merge } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent,
  TemplateService,
  SaveTemplateComponent
} from '@nrwl/test-setup';
import {
  ValuesExchangeProvider,
  NotificationService,
  TestListService,
  TestTypeValue,
  DialogService,
  TEST_STATE,
  AGENT_MODEL
} from '@nrwl/common-lib';
import { TwampTestService } from './twamp-test.service';
import { TwampTestComponent } from './components/twamp-test/twamp-test-setup/twamp-test.component';
import { TwampTestAdvancedComponent } from './components/twamp-test/twamp-test-advanced/twamp-test-advanced.component';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import {
  TwampConfigFieldMapping as ConfigFieldMapping,
  TwampConfigFieldMapping
} from './components/models/model';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'network-test',
  providers: [TwampTestService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class TwampTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  setupId: string;
  testId: string;
  mode: string;
  agentBasedConfig: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: TwampTestService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  editMode: boolean;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  currentObj: any; //eslint-disable-line
  templateId: string;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    router: Router,
    setupService: TwampTestService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly exchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly templateService: TemplateService,
    private readonly notificationService: NotificationService,
    private readonly testListService: TestListService,
    private readonly navigationProvider: NavigationProvider,
    private dialog: MatDialog,
    private readonly dialogService: DialogService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.templateId = defaultParams?.templateId;
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
    if (this.unsubscribe) {
      this.unsubscribe.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.setupService.onNext();
    this.editMode = this.setupService.editMode;
    this.validateTestParams();
  }

  validateTestParams(): void {
    let maxFlows = null;
    const phyId =
      this.setupService.output[TwampConfigFieldMapping.SELECTED_ORIGINATOR].deviceData?.phyId;
    const capabilities =
      this.setupService.output[TwampConfigFieldMapping.SELECTED_ORIGINATOR].deviceData
        ?.capabilities;
    if (capabilities) {
      if (phyId) {
        const twampInitiatorItem = capabilities['twamp-initiator-list']?.find(
          item => item['phy-id'] === phyId
        );
        if (twampInitiatorItem) {
          maxFlows =
            twampInitiatorItem['max-flows-per-port'] || twampInitiatorItem['max-flows-per-test'];
        }
      } else {
        maxFlows =
          capabilities['twamp-initiator']?.['max-flows-per-test'] ||
          capabilities['twamp-initiator']?.['max-flows'];
      }
    }
    if (maxFlows) {
      let testFlows = null;
      if (this.setupService.output[ConfigFieldMapping.SELECTED_TAB] === 0) {
        testFlows =
          this.setupService.output[TwampConfigFieldMapping.SELECTED_DESTINATION].length *
          this.setupService.output[ConfigFieldMapping.COMMON_FLOWS].length;
      } else {
        testFlows = Object.values(
          this.setupService.output[ConfigFieldMapping.SPECIFIC_FLOWS]
        ).reduce(
          (accumulator: number, currentValue: Array<any>) => accumulator + currentValue.length,
          0
        );
      }
      if (testFlows && parseInt(testFlows) > parseInt(maxFlows)) {
        this.dialogService.showInfo(
          'information',
          'body',
          'You have reached the maximum number of flows (' +
            maxFlows +
            ') for a test.</p><p>You must remove flows to create a test.</p>',
          'Error - Exceeds Max flows limit'
        );
        return;
      }
    }
    this.createTwampTest();
  }

  createTwampTest(): void {
    const tWampLmapObject = this.setupService.createLMAPObject();
    const message = this.editMode ? 'Updating test' : 'Creating test';
    this.setupService.openTestStatusDialog(message, this.editMode ? 'Update' : 'Add');
    if (!this.setupService.editMode) {
      this.subscription = this.setupService._saveControllerTest(tWampLmapObject).subscribe(
        response => {
          const message = 'Twamp test has been created';
          this.notificationService.onSuccess(message);
          this.testListService.updateTestResponse();
          this.setupService.dialogRef.close();
          this.toggleClose();
          this.exchange.notifyListeners('new_test', response);
          this.openTestRunning(response.data.id);
        },
        error => {
          const message = error?.error?.message
            ? 'Failed to create twamp test. Reason: ' + error.error?.message
            : 'Failed to create twamp test';
          this.notificationService.onFail(message);
          this.setupService.dialogRef.close();
          this.toggleClose();
        }
      );
    } else {
      let applyValue = 'next-run';
      const selectedOriginator = this.setupService.output[ConfigFieldMapping.SELECTED_ORIGINATOR];
      const originatorAgentType = selectedOriginator.getModel();
      const scheduleId = this.setupService.defaultParamsById['scheduleId'];
      const testStatus = this.testListService.getTestListById(scheduleId)?.getTestStatus();
      if (testStatus === TEST_STATE.RUNNING) {
        applyValue =
          originatorAgentType === AGENT_MODEL.JMEP_1G ? 'next-run-restart-now' : 'immediate';
      }
      this.subscription = this.setupService
        ._updateControllerTest({
          opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: applyValue },
          config: tWampLmapObject
        })
        .subscribe(
          response => {
            const message = 'Twamp test has been updated';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
          },
          error => {
            const message = error?.error?.message
              ? 'Failed to update twamp test. Reason: ' + error.error?.message
              : 'Failed to update twamp test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
    }
  }

  openTestRunning(testId: string): void {
    this.navigationProvider.navigateToDashboard(['test-running'], {
      queryParams: {
        testId: testId,
        testName: this.setupService.output.alias,
        testType: TestTypeValue.TWAMP,
        source: null,
        layeredSource: [],
        queryParamsHandling: 'merge',
        replaceUrl: true,
        hideTestSetup: true // to hide the testsetup wizard panel
      }
    });
  }

  closeSetupPage(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
    this.setupService.updateBulkTestStatusDialog(
      this.editMode ? 'Twamp test has been updated' : 'Twamp test has been created',
      '',
      true
    );
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const lmapObject = this.setupService.createLMAPObject();
    this.setupService._downloadTestConfig(lmapObject);
  }

  saveTestConfig(): void {
    this.templateService.setTemplateId(this.templateId);
    this.setupService.onNext();
    let configParams = this.setupService.getTwampTestConfig();
    const optionsParams = this.setupService.getTestOptionsForSaveTemplate();
    configParams = { ...configParams, ...optionsParams };
    const selectedOriginator = this.setupService.output[ConfigFieldMapping.SELECTED_ORIGINATOR];
    const selectedDestination =
      this.setupService.output[ConfigFieldMapping.SELECTED_DESTINATION_AGENTS];
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      selectedOriginator,
      selectedDestination
    );
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.TWAMP }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  openTemplatesDialog(service: any): void {
    service.toggleClose();
    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.TWAMP,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }

  getSetupService(): TwampTestService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getFactoryResolver(): ComponentFactoryResolver {
    return this.factoryResolver;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'twamp-test.component.html':
        return TwampTestComponent;
      case 'twamp-test-advanced.component.html':
        return TwampTestAdvancedComponent;

      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
